import { computed, ref } from '@vue/composition-api';
import { useAsyncResult } from '@ligo/shared/utils';
import { CardPlan } from '../../../models';
import { useI18n, useStore } from '../../../composables';
import { useMembershipInfo } from '../../utils/membership-info';
import { MembershipType, SubscriptionType } from '@ligo/shared/auth';
import {
  SubscriptionService,
  MembershipSlug
} from '@ligo/dashboard/customer/store';

function isValidJSON(str: string) {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}

function isValidMembership(
  membership: MembershipType | SubscriptionType
): boolean {
  return (
    membership.description &&
    membership.name &&
    (membership.billing_interval || membership.billing_period) &&
    isValidJSON(membership.description)
  );
}

function subscriptionToCardPlan(
  subscription: MembershipType | SubscriptionType
): CardPlan {
  const localeMapper = { 'en-us': 'en', nl: 'nl' };
  const lang = localeMapper[useI18n().locale];
  const description = JSON.parse(subscription.description)[lang];
  const slug = 'slug' in subscription ? subscription.slug : undefined;

  return {
    id: subscription.id,
    name: subscription.name.trim().split(' ').slice(0, -1).join(' '),
    billingInterval: subscription.billing_period,
    items: description.features,
    price: subscription.price,
    subtitle: description.subtitle,
    slug,
    title_description: description.title_description,
    billing_description: description.billing_description,
    border: description.border,
    title: description.title,
    cta: description.cta,
    free: subscription.price == 0
  };
}

export function usePlans() {
  const store = useStore();
  store.dispatch('authentication/loadUserInfo');

  const { state, loading } = useAsyncResult(SubscriptionService.list);

  console.log('BEFORE', loading.value);
  const { currentType: currentPlan, hasMembership } = useMembershipInfo();
  console.log('LOADING', loading.value, currentPlan.value);

  const currentPlanCard = computed(() => {
    const current = currentPlan.value;
    return current && isValidMembership(current)
      ? subscriptionToCardPlan(current)
      : undefined;
  });

  const showLegacyBanner = ref(hasMembership.value);

  const plans = computed(() =>
    state.value
      .map((p) => {
        if (p.id == currentPlan.value?.id) {
          showLegacyBanner.value = false;
        }
        return p;
      })
      .filter((v) => !!v.description && v.id != currentPlan.value?.id)
      .map((p) => subscriptionToCardPlan(p))
  );

  const activePlan = ref();

  function displayPlan(plan: CardPlan) {
    if (plan.free) {
      if (!hasMembership.value) return true;
      return false;
    }
    return true;
  }

  const visiblePlans = computed(() => {
    const tail = plans.value.filter((p) => {
      return displayPlan(p);
    });
    if (currentPlanCard.value) return [currentPlanCard.value, ...tail];
    return tail;
  });

  return {
    loading,
    plans,
    visiblePlans,
    currentPlan,
    activePlan,
    showLegacyBanner
  };
}
